import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Textarea } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import {
  useApproveContentItem,
  useContentItem,
  usePushWebsiteReviewDraft,
  useSaveWebsiteReviewSnapshot,
  useWebsiteReview,
} from '../../hooks/useContent';
import { useAuth } from '../../hooks/useAuth';
import { formatDateTime } from '../../utils/dateFormat';
import type { WebsiteReviewResponse } from '../../api/content';
import type { ReviewMeta } from './reviewers';

type EditorSource = 'manual_edit' | 'paste_replacement' | 'section_edit';
type PathSegment = string | number;

const HIDDEN_STRUCTURAL_KEYS = new Set([
  '_key',
  '_ref',
  '_type',
  '_id',
  'internal',
]);

const LOCKED_CONTENT_KEYS = new Set([
  'icon',
  'linkType',
  'category',
  'number',
]);

const LONG_TEXT_KEYS = new Set([
  'answer',
  'body',
  'cardDescription',
  'description',
  'excerpt',
  'quote',
  'seoDescription',
  'shortDefinition',
  'socialProof',
]);

const SECTION_LABELS: Record<string, string> = {
  cta: 'Call to action',
  faq: 'FAQ',
  howItWorks: 'How it works',
  seo: 'SEO',
};

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function stableSerialize(value: unknown): string {
  if (value === null || value === undefined) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableSerialize((value as Record<string, unknown>)[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

function valuesEqual(a: unknown, b: unknown): boolean {
  return stableSerialize(a) === stableSerialize(b);
}

function humanizeKey(key: string): string {
  return (SECTION_LABELS[key] ?? key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeFieldOrderPath(path: PathSegment[]): string {
  return path
    .map((segment) => (typeof segment === 'number' ? '*' : segment))
    .join('.');
}

function orderedChildKeys(
  currentValue: unknown,
  draftValue: unknown,
  orderPath: PathSegment[],
  fieldOrderByPath: Record<string, string[]>,
): string[] {
  const order: string[] = [];
  const preferredOrder = fieldOrderByPath[normalizeFieldOrderPath(orderPath)] ?? [];
  const currentKeys = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
    ? Object.keys(currentValue as Record<string, unknown>)
    : [];
  const draftKeys = draftValue && typeof draftValue === 'object' && !Array.isArray(draftValue)
    ? Object.keys(draftValue as Record<string, unknown>)
    : [];
  for (const key of preferredOrder) {
    if ((currentKeys.includes(key) || draftKeys.includes(key)) && !order.includes(key)) {
      order.push(key);
    }
  }
  for (const key of currentKeys) {
    if (!order.includes(key)) order.push(key);
  }
  for (const key of draftKeys) {
    if (!order.includes(key)) order.push(key);
  }
  return order;
}

function deriveItemLabel(value: unknown, index: number): string {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    const candidate = record.title ?? record.question ?? record.label ?? record.heading ?? record.authorName;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return `Item ${index + 1}`;
}

function updateAtPath(value: unknown, path: PathSegment[], nextValue: unknown): unknown {
  if (path.length === 0) return nextValue;
  const [segment, ...rest] = path;

  if (Array.isArray(value)) {
    const nextArray = [...value];
    const index = Number(segment);
    nextArray[index] = updateAtPath(nextArray[index], rest, nextValue);
    return nextArray;
  }

  const nextRecord = value && typeof value === 'object'
    ? { ...(value as Record<string, unknown>) }
    : {};
  nextRecord[String(segment)] = updateAtPath(nextRecord[String(segment)], rest, nextValue);
  return nextRecord;
}

function shouldHideField(key: string): boolean {
  return HIDDEN_STRUCTURAL_KEYS.has(key);
}

function isLockedField(key: string): boolean {
  return LOCKED_CONTENT_KEYS.has(key);
}

function isPrimitiveEditable(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

function isArrayOfEditablePrimitives(value: unknown): value is Array<string | number> {
  return Array.isArray(value) && value.every((item) => typeof item === 'string' || typeof item === 'number');
}

function countStructuralFields(value: unknown): number {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 0;
  let count = 0;
  for (const key of Object.keys(value as Record<string, unknown>)) {
    if (shouldHideField(key) || isLockedField(key)) count += 1;
  }
  return count;
}

export function WebsiteReviewPanel({
  contentItemId,
}: {
  contentItemId: string;
}) {
  const reviewQuery = useWebsiteReview(contentItemId);
  const itemQuery = useContentItem(contentItemId);
  const saveMutation = useSaveWebsiteReviewSnapshot();
  const pushMutation = usePushWebsiteReviewDraft();
  const approveMutation = useApproveContentItem();
  const { user } = useAuth();

  async function handleSave(snapshot: Record<string, unknown>, source: EditorSource) {
    await saveMutation.mutateAsync({
      contentItemId,
      snapshot,
      source,
    });
  }

  async function handlePushDraft() {
    const result = await pushMutation.mutateAsync({ contentItemId });
    if ('requiresConfirmation' in result) {
      const otherLabel =
        result.otherMode === 'scratch'
          ? 'From scratch'
          : result.otherMode === 'manual'
            ? 'Manual import'
            : 'From live page';
      const pushedAt = result.otherPushedAt
        ? formatDateTime(result.otherPushedAt)
        : 'previously';
      const confirmed = window.confirm(
        `The "${otherLabel}" version of this page ("${result.otherTitle}") was pushed to Sanity ${pushedAt}.\n\n` +
          `Both versions target the same Sanity draft, so pushing now will overwrite that draft. Continue?`,
      );
      if (!confirmed) return;
      await pushMutation.mutateAsync({ contentItemId, confirmOverwrite: true });
    }
  }

  if (reviewQuery.isLoading) {
    return (
      <Card className="p-4 flex items-center gap-3">
        <Spinner />
        <span className="text-body-sm text-text-secondary">Loading structured website review…</span>
      </Card>
    );
  }

  if (reviewQuery.error instanceof Error) {
    return <Alert variant="error">{reviewQuery.error.message}</Alert>;
  }

  const review = reviewQuery.data;
  if (!review) return null;

  const item = itemQuery.data;
  const reviewMeta = (item?.metadata as { review?: ReviewMeta } | undefined)?.review;
  const itemStatus = item?.status;
  const isAdmin = user?.role === 'admin';
  const inReview = itemStatus === 'review' && !!reviewMeta?.reviewers?.length;
  const currentIndex = reviewMeta?.currentIndex ?? 0;
  const currentReviewer = reviewMeta?.reviewers[currentIndex];
  const isCurrentUserCurrentReviewer =
    !!user &&
    !!currentReviewer &&
    currentReviewer.status === 'pending' &&
    (currentReviewer.email.toLowerCase() === user.email.toLowerCase() ||
      currentReviewer.userId === user.id);
  const userAlreadyApproved =
    !!user &&
    !!reviewMeta?.reviewers.some(
      (r) =>
        r.status === 'approved' &&
        (r.email.toLowerCase() === user.email.toLowerCase() || r.userId === user.id),
    );

  return (
    <div className="space-y-4">
      {pushMutation.data && !('requiresConfirmation' in pushMutation.data) && (
        <Alert variant="success" title="Draft saved to Sanity">
          <p>Sanity draft ID: <code>{pushMutation.data.sanityDocumentId}</code></p>
          <p className="mt-1 text-body-xs text-text-secondary">
            {pushMutation.data.activatedRuleIds.length > 0
              ? `Promoted ${pushMutation.data.activatedRuleIds.length} saved rule candidate${pushMutation.data.activatedRuleIds.length === 1 ? '' : 's'} into Generation Rules.`
              : 'No new saved rule candidates were promoted into Generation Rules from this draft push.'}
          </p>
          {pushMutation.data.duplicateRuleIds.length > 0 && (
            <p className="mt-1 text-body-xs text-text-secondary">
              {pushMutation.data.duplicateRuleIds.length} candidate
              {pushMutation.data.duplicateRuleIds.length === 1 ? ' was' : 's were'} skipped because equivalent guidance already existed.
            </p>
          )}
        </Alert>
      )}

      {pushMutation.error instanceof Error && (
        <Alert variant="error">{pushMutation.error.message}</Alert>
      )}

      {approveMutation.error instanceof Error && (
        <Alert variant="error">{approveMutation.error.message}</Alert>
      )}

      <WebsiteSnapshotDiffView
        review={review}
        onSave={handleSave}
        saving={saveMutation.isPending}
      />

      <Card className="p-4 space-y-3">
        <div className="space-y-1">
          <h3 className="text-body-md font-medium text-text-primary">Review &amp; publish</h3>
          <p className="text-body-xs text-text-tertiary">
            {inReview
              ? isCurrentUserCurrentReviewer
                ? 'When you\'re done editing, click "Mark as reviewed". The next reviewer will be DM\'d on Slack automatically.'
                : userAlreadyApproved
                  ? `You've already marked this reviewed. Waiting for ${currentReviewer?.name ?? 'the next reviewer'} to sign off.`
                  : `Waiting for ${currentReviewer?.name ?? 'the current reviewer'} to mark this reviewed.`
              : itemStatus === 'approved' || itemStatus === 'published'
                ? 'Status is Approved. Send the reviewed version to Sanity as the draft.'
                : 'No active review queue. Assign reviewers above to start the review chain.'}
          </p>
          <p className="text-body-xs text-text-tertiary">
            Push to Sanity unlocks once status is Approved — either by every reviewer signing off, or
            {isAdmin
              ? ' by an admin setting the status manually from the dropdown above.'
              : ' by an admin overriding the status manually.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => approveMutation.mutate(contentItemId)}
            disabled={
              !inReview ||
              !isCurrentUserCurrentReviewer ||
              approveMutation.isPending
            }
          >
            {approveMutation.isPending ? 'Marking as reviewed…' : 'Mark as reviewed'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void handlePushDraft();
            }}
            disabled={
              pushMutation.isPending ||
              (itemStatus !== 'approved' && itemStatus !== 'published')
            }
          >
            {pushMutation.isPending ? 'Pushing draft…' : 'Push to Sanity draft'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function WebsiteSnapshotDiffView({
  review,
  onSave,
  saving,
}: {
  review: WebsiteReviewResponse;
  onSave: (snapshot: Record<string, unknown>, source: EditorSource) => void | Promise<void>;
  saving: boolean;
}) {
  const orderedSections = useMemo(() => {
    const sectionDiffByKey = new Map(review.diff.perSection.map((section) => [section.sectionKey, section]));
    return review.sectionOrder.map((sectionKey) => ({
      sectionKey,
      section: sectionDiffByKey.get(sectionKey) ?? {
        sectionKey,
        kind: 'unchanged' as const,
        fields: [],
      },
    }));
  }, [review]);

  const initialDrafts = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const { sectionKey } of orderedSections) {
      const proposed = review.proposed[sectionKey];
      const existing = review.existing[sectionKey];
      out[sectionKey] = cloneValue(proposed === undefined ? existing : proposed);
    }
    return out;
  }, [orderedSections, review.proposed, review.existing]);

  const [drafts, setDrafts] = useState<Record<string, unknown>>(initialDrafts);

  useEffect(() => {
    setDrafts(initialDrafts);
  }, [initialDrafts]);

  function setSectionDraft(sectionKey: string, nextValue: unknown) {
    setDrafts((prev) => ({ ...prev, [sectionKey]: nextValue }));
  }

  function resetSectionDraft(sectionKey: string) {
    setDrafts((prev) => ({ ...prev, [sectionKey]: initialDrafts[sectionKey] }));
  }

  async function saveAllDirtySections(source: EditorSource) {
    const merged: Record<string, unknown> = { ...review.proposed };
    for (const { sectionKey } of orderedSections) {
      if (!valuesEqual(drafts[sectionKey], initialDrafts[sectionKey])) {
        merged[sectionKey] = drafts[sectionKey];
      }
    }
    await onSave(merged, source);
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-body-md font-medium text-text-primary">Current vs proposed</h3>
          {review.diff.summary && (
            <p className="text-body-sm text-text-secondary mt-1">{review.diff.summary}</p>
          )}
          <p className="text-body-xs text-text-tertiary mt-1">
            Sections follow the Sanity page order. Text content is editable here, while structure, references, icons, and section wiring stay locked to the live page. Clicking any Save button persists every section's latest edits in one go.
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          <div className="flex gap-1">
            <Badge variant="info">{review.diff.sectionsChanged.length} changed</Badge>
            <Badge variant="success">{review.diff.sectionsAdded.length} added</Badge>
            <Badge variant="error">{review.diff.sectionsRemoved.length} removed</Badge>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {orderedSections.map(({ sectionKey, section }) => (
          <StructuredSectionEditor
            key={sectionKey}
            sectionKey={sectionKey}
            sectionKind={section.kind}
            existingValue={review.existing[sectionKey]}
            initialDraft={initialDrafts[sectionKey]}
            draftValue={drafts[sectionKey]}
            fieldOrderByPath={review.fieldOrderByPath}
            saving={saving}
            onDraftChange={(nextValue) => setSectionDraft(sectionKey, nextValue)}
            onResetSection={() => resetSectionDraft(sectionKey)}
            onSave={saveAllDirtySections}
          />
        ))}
      </div>
    </Card>
  );
}

function StructuredSectionEditor({
  sectionKey,
  sectionKind,
  existingValue,
  initialDraft,
  draftValue,
  fieldOrderByPath,
  saving,
  onDraftChange,
  onResetSection,
  onSave,
}: {
  sectionKey: string;
  sectionKind: 'added' | 'removed' | 'changed' | 'unchanged';
  existingValue: unknown;
  initialDraft: unknown;
  draftValue: unknown;
  fieldOrderByPath: Record<string, string[]>;
  saving: boolean;
  onDraftChange: (nextValue: unknown) => void;
  onResetSection: () => void;
  onSave: (source: EditorSource) => void | Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [initialDraft]);

  const dirty = !valuesEqual(draftValue, initialDraft);
  const structuralFields = countStructuralFields(initialDraft);

  async function handleSave() {
    setError(null);
    try {
      await onSave('section_edit');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  return (
    <Card className="p-4 space-y-4 border border-border">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-body-md font-medium text-text-primary">{humanizeKey(sectionKey)}</h4>
            <Badge variant={badgeVariantFor(sectionKind)}>{sectionKind}</Badge>
          </div>
          {structuralFields > 0 && (
            <p className="text-body-xs text-text-tertiary">
              {structuralFields} structural field{structuralFields === 1 ? '' : 's'} preserved from Sanity and locked here.
            </p>
          )}
        </div>
        {dirty && (
          <Badge variant="warning">Unsaved changes</Badge>
        )}
      </div>

      <SectionNodeEditor
        label={humanizeKey(sectionKey)}
        currentValue={existingValue}
        draftValue={draftValue}
        dataPath={[]}
        orderPath={[sectionKey]}
        fieldOrderByPath={fieldOrderByPath}
        onChange={(dataPath, nextValue) => onDraftChange(updateAtPath(draftValue, dataPath, nextValue))}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? 'Saving…' : `Save ${humanizeKey(sectionKey)}`}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty}
          onClick={() => {
            onResetSection();
            setError(null);
          }}
        >
          Reset section
        </Button>
      </div>
    </Card>
  );
}

function SectionNodeEditor({
  label,
  currentValue,
  draftValue,
  dataPath,
  orderPath,
  fieldOrderByPath,
  onChange,
}: {
  label: string;
  currentValue: unknown;
  draftValue: unknown;
  dataPath: PathSegment[];
  orderPath: PathSegment[];
  fieldOrderByPath: Record<string, string[]>;
  onChange: (path: PathSegment[], nextValue: unknown) => void;
}) {
  const resolvedDraft = draftValue === undefined ? currentValue : draftValue;

  if (isArrayOfEditablePrimitives(resolvedDraft)) {
    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    return (
      <div className="space-y-3">
        <div className="text-body-sm font-medium text-text-primary">{label}</div>
        {resolvedDraft.map((item, index) => (
          <EditablePrimitiveField
            key={`${label}-${index}`}
            label={`${humanizeKey(label)} ${index + 1}`}
            currentValue={currentArray[index]}
            draftValue={item}
            fieldKey={String(dataPath[dataPath.length - 1] ?? label)}
            onChange={(nextValue) => onChange([...dataPath, index], nextValue)}
          />
        ))}
      </div>
    );
  }

  if (Array.isArray(resolvedDraft)) {
    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    return (
      <div className="space-y-3">
        <div className="text-body-sm font-medium text-text-primary">{label}</div>
        <div className="space-y-3">
          {resolvedDraft.map((item, index) => (
            <div key={`${label}-${index}`} className="rounded-xl border border-border bg-surface-subtle p-4 space-y-3">
              <div className="text-body-sm font-medium text-text-primary">
                {deriveItemLabel(item, index)}
              </div>
              <SectionNodeEditor
                label={`${label} ${index + 1}`}
                currentValue={currentArray[index]}
                draftValue={item}
                dataPath={[...dataPath, index]}
                orderPath={[...orderPath, index]}
                fieldOrderByPath={fieldOrderByPath}
                onChange={onChange}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (resolvedDraft && typeof resolvedDraft === 'object') {
    const keys = orderedChildKeys(currentValue, resolvedDraft, orderPath, fieldOrderByPath);
    const visibleKeys = keys.filter((key) => !shouldHideField(key));
    if (visibleKeys.length === 0) return null;

    return (
      <div className="space-y-4">
        {visibleKeys.map((key) => {
          const nextCurrent = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
            ? (currentValue as Record<string, unknown>)[key]
            : undefined;
          const nextDraft = resolvedDraft && typeof resolvedDraft === 'object' && !Array.isArray(resolvedDraft)
            ? (resolvedDraft as Record<string, unknown>)[key]
            : undefined;

          if (isLockedField(key)) {
            return null;
          }

          const effectiveValue = nextDraft === undefined ? nextCurrent : nextDraft;
          if (isPrimitiveEditable(effectiveValue)) {
            return (
              <EditablePrimitiveField
                key={[...dataPath, key].join('.')}
                label={humanizeKey(key)}
                currentValue={nextCurrent}
                draftValue={effectiveValue}
                fieldKey={key}
                onChange={(nextValue) => onChange([...dataPath, key], nextValue)}
              />
            );
          }

          if (isArrayOfEditablePrimitives(effectiveValue)) {
            return (
              <SectionNodeEditor
                key={[...dataPath, key].join('.')}
                label={humanizeKey(key)}
                currentValue={nextCurrent}
                draftValue={effectiveValue}
                dataPath={[...dataPath, key]}
                orderPath={[...orderPath, key]}
                fieldOrderByPath={fieldOrderByPath}
                onChange={onChange}
              />
            );
          }

          if (effectiveValue && typeof effectiveValue === 'object') {
            return (
              <div key={[...dataPath, key].join('.')} className="space-y-3">
                <div className="text-body-sm font-medium text-text-primary">{humanizeKey(key)}</div>
                <div className="rounded-xl border border-border bg-surface-primary p-4">
                  <SectionNodeEditor
                    label={humanizeKey(key)}
                    currentValue={nextCurrent}
                    draftValue={effectiveValue}
                    dataPath={[...dataPath, key]}
                    orderPath={[...orderPath, key]}
                    fieldOrderByPath={fieldOrderByPath}
                    onChange={onChange}
                  />
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  }

  if (isPrimitiveEditable(resolvedDraft)) {
    return (
      <EditablePrimitiveField
        label={label}
        currentValue={currentValue}
        draftValue={resolvedDraft}
        fieldKey={String(dataPath[dataPath.length - 1] ?? label)}
        onChange={(nextValue) => onChange(dataPath, nextValue)}
      />
    );
  }

  return null;
}

function EditablePrimitiveField({
  label,
  currentValue,
  draftValue,
  fieldKey,
  onChange,
}: {
  label: string;
  currentValue: unknown;
  draftValue: string | number;
  fieldKey: string;
  onChange: (nextValue: string) => void;
}) {
  const currentText = currentValue === undefined || currentValue === null ? '(empty)' : String(currentValue);
  const draftText = draftValue === undefined || draftValue === null ? '' : String(draftValue);
  const multiline = LONG_TEXT_KEYS.has(fieldKey)
    || draftText.length > 80
    || currentText.length > 80
    || draftText.includes('\n')
    || currentText.includes('\n');
  const changed = currentText !== draftText;
  const currentBoxRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [minHeight, setMinHeight] = useState(52);

  useLayoutEffect(() => {
    if (!multiline || !currentBoxRef.current) return;
    setMinHeight(Math.max(currentBoxRef.current.offsetHeight, 52));
  }, [currentText, multiline]);

  useLayoutEffect(() => {
    if (!multiline || !textareaRef.current) return;
    const textarea = textareaRef.current;
    textarea.style.height = 'auto';
    textarea.style.minHeight = `${minHeight}px`;
    textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`;
  }, [draftText, multiline, minHeight]);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary">{label}</div>
          {changed && <Badge variant="info">Changed</Badge>}
        </div>
        <div
          ref={currentBoxRef}
          className="rounded-xl border border-border bg-surface-subtle px-4 py-3 text-body-sm text-text-secondary whitespace-pre-wrap break-words min-h-[52px]"
        >
          {currentText}
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-body-xs font-medium uppercase tracking-wide text-text-tertiary">Proposed update</div>
        <Textarea
          ref={textareaRef}
          rows={multiline ? Math.max(3, draftText.split('\n').length + 1) : 1}
          value={draftText}
          onChange={(e) => onChange(e.target.value)}
          className="resize-y overflow-hidden rounded-2xl px-4 py-3"
          style={{ minHeight }}
        />
      </div>
    </div>
  );
}

function badgeVariantFor(kind: 'added' | 'removed' | 'changed' | 'unchanged'): 'success' | 'error' | 'info' | 'default' {
  if (kind === 'added') return 'success';
  if (kind === 'removed') return 'error';
  if (kind === 'changed') return 'info';
  return 'default';
}
